/**
 * Loads everything waiting on a person (shared/needsYou) and carries out the
 * one-tap actions that settle it.
 *
 * Each source loads on its own. One that fails is named in `errors` rather
 * than quietly missing from the list: an empty queue has to mean nothing is
 * waiting, never that something could not be read.
 */

import { pb, escapeFilterValue } from '../lib/pocketbase'
import { ensureUserAgency } from '../utils/ensureAgency'
import { buildNeedsYou, type NeedsAction, type NeedsItem, type NeedsClient, type NeedsContract, type NeedsRead, type NeedsInquiry } from '../shared/needsYou'
import { chooseEspnValue, keepRecordValue } from '../shared/profileSync'
import { getAgencyMoneyCalendar } from './moneyCalendar'
import { getBalances, reverseBookedCommission } from './balances'
import { listContactSuggestions, acceptContactSuggestion, skipContactSuggestion } from './contactSuggestions'
import { listBookChanges, decideBookChanges } from './addressBookChanges'
import { decideContractRead } from './contractTerms'
import { logMoneyItem } from './moneyLog'
import { updateContract } from './contracts'
import { playingContractFilter } from '../shared/standingRules'
import { settleHonor, sweepProblems, type HonorFind } from '../shared/honors'
import { nilGoStates, easternDay, type NilGoStatus } from '../shared/nilGo'
import { nilGoAction } from './nilGo'

export interface NeedsYou {
  items: NeedsItem[]
  /** Sources that could not be read, by name and reason. */
  errors: string[]
}

async function settle<T>(label: string, errors: string[], run: () => Promise<T>): Promise<T | null> {
  try {
    return await run()
  } catch (err) {
    errors.push(`${label}: ${(err as Error).message}`)
    return null
  }
}

/** The partner-page inquiries waiting on a person, and the decision on one (api/partner-pages.ts). */
async function partnerPages(body?: Record<string, unknown>): Promise<Record<string, any>> {
  const res = await fetch(body ? '/api/partner-pages' : '/api/partner-pages?inquiries=1', {
    method: body ? 'POST' : 'GET',
    headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), Authorization: `Bearer ${pb.authStore.token}` },
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => null)
  if (!res.ok || !data) throw new Error(data?.error || `Partner inquiries answered ${res.status}.`)
  return data
}

/** @param canSeeMoney False for assistants, whom the transactions rules refuse. */
export async function getNeedsYou(canSeeMoney: boolean): Promise<NeedsYou> {
  const agencyId = await ensureUserAgency()
  const agency = `agency_id = "${escapeFilterValue(agencyId)}"`
  const errors: string[] = []

  const [clientRows, contractRows, calendar, balances, readRows, suggestions, bookChanges, inquiries, honors, sweep, nil] = await Promise.all([
    settle('Athletes', errors, () => pb.collection('clients').getFullList<Record<string, any>>({ filter: agency, fields: 'id,full_name,first_name,last_name,league,cross_checks,profile_sync,position,jersey_number,height,weight,date_of_birth' })),
    settle('Contracts', errors, () => pb.collection('contracts').getFullList<Record<string, any>>({ filter: `${agency} && status = "active" && (${playingContractFilter()})`, fields: 'id,title,client_id,cross_checks' })),
    canSeeMoney ? settle('Money', errors, () => getAgencyMoneyCalendar(null)) : Promise.resolve(null),
    canSeeMoney ? settle('Books', errors, () => getBalances()) : Promise.resolve(null),
    settle('Contract documents', errors, () => pb.collection('contract_term_extractions').getFullList<Record<string, any>>({ filter: `${agency} && status = "needs_review"`, fields: 'id,contract_id,plan' })),
    settle('Contact suggestions', errors, () => listContactSuggestions()),
    settle('iPhone contacts', errors, () => listBookChanges()),
    settle('Partner inquiries', errors, async () => (await partnerPages()).inquiries as NeedsInquiry[]),
    settle('Honors', errors, () => pb.collection('honor_finds').getFullList<Record<string, any>>({ filter: `${agency} && status = "new"`, fields: 'id,client_id,week,headlines', sort: '-week' })),
    settle('Honors sweep', errors, async () => (await pb.collection('honor_sweeps').getList(1, 1, { sort: '-week', fields: 'week,problems' })).items[0] ?? null),
    settle('NIL Go', errors, async () => {
      const [deals, records] = await Promise.all([
        pb.collection('deals').getFullList<Record<string, any>>({ filter: `${agency} && stage != "Lost"`, fields: 'id,title,client_id,organization_id,value,stage,deal_type,stage_changed_at' }),
        pb.collection('nil_go_records').getFullList<Record<string, any>>({ filter: agency }),
      ])
      return { deals, records }
    }),
  ])

  const clients: NeedsClient[] = (clientRows ?? []).map(c => ({
    id: c.id,
    name: c.full_name || [c.first_name, c.last_name].filter(Boolean).join(' '),
    cross_checks: c.cross_checks ?? null,
    profile_sync: c.profile_sync ?? null,
    values: {
      position: c.position || null, jersey_number: c.jersey_number || null, height: c.height || null, weight: c.weight || null,
      date_of_birth: c.date_of_birth ? String(c.date_of_birth).slice(0, 10) : null,
    },
  }))
  const names = new Map(clients.map(c => [c.id, c.name]))
  const contracts: NeedsContract[] = (contractRows ?? []).map(c => ({ id: c.id, title: c.title || null, client_id: c.client_id || null, cross_checks: c.cross_checks ?? null }))
  const contractById = new Map(contracts.map(c => [c.id, c]))
  const reads: NeedsRead[] = (readRows ?? []).map(r => {
    const k = contractById.get(r.contract_id)
    return {
      id: r.id, contract_id: r.contract_id, contract_title: k?.title ?? null,
      client_id: k?.client_id ?? null, client_name: k?.client_id ? names.get(k.client_id) ?? null : null,
      reasons: Array.isArray(r.plan?.reasons) ? r.plan.reasons : [],
    }
  })

  const items = buildNeedsYou({
    clients,
    contracts,
    missed: calendar ? calendar.missed : null,
    dueToRecord: calendar ? calendar.due_to_record : null,
    gaps: calendar ? calendar.gaps : null,
    balances: balances ? balances.findings : null,
    reads,
    suggestions: (suggestions ?? []).map(s => ({ id: s.id, card_name: s.card_name, display_value: s.display_value, field: s.field, person: s.person })),
    bookChanges: (bookChanges?.proposals ?? []).map(b => ({ id: b.id, kind: b.kind, display_value: b.display_value, card_name: b.card_name, person: b.person })),
    inquiries: inquiries ?? [],
    honors: (honors ?? []) as unknown as HonorFind[],
    nilGo: nil && clientRows ? nilGoStates(clientRows as any[], nil.deals as any[], nil.records as any[], easternDay(new Date()) as string) : [],
  })
  errors.push(...sweepProblems(sweep))
  return { items, errors }
}

/** Remembers that a person looked at this exact finding and accepts it. */
async function acknowledge(collection: 'contracts' | 'clients', recordId: string, check: string, key: string, userId: string) {
  const fresh = await pb.collection(collection).getOne<Record<string, any>>(recordId, { fields: 'cross_checks' })
  const cross = fresh.cross_checks && typeof fresh.cross_checks === 'object' ? fresh.cross_checks : {}
  await pb.collection(collection).update(recordId, {
    cross_checks: { ...cross, acknowledged: { ...(cross.acknowledged ?? {}), [check]: { key, by: userId, at: new Date().toISOString() } } },
  })
}

/** Carries out one action. Throws with what went wrong; the caller shows it. Returns what to say. */
export async function performNeedsAction(action: NeedsAction, user: { id: string; agency_id: string }): Promise<string> {
  const now = new Date().toISOString()
  switch (action.type) {
    case 'acknowledge':
      await acknowledge(action.collection, action.recordId, action.check, action.key, user.id)
      return 'Marked checked. It comes back only if the finding changes.'
    case 'use_value': {
      if (action.field === 'date_of_birth') {
        await pb.collection('clients').update(action.clientId, { date_of_birth: `${action.value} 00:00:00.000Z` })
        return `Date of birth set to ${action.value}. The birthday on the calendar moves on the next schedule run.`
      }
      const fresh = await pb.collection('clients').getOne<Record<string, any>>(action.clientId, { fields: 'profile_sync' })
      const sync = fresh.profile_sync && typeof fresh.profile_sync === 'object' ? fresh.profile_sync : {}
      await pb.collection('clients').update(action.clientId, { [action.field]: action.value, profile_sync: { ...sync, [action.field]: chooseEspnValue(action.value, now) } })
      return `Set to ${action.value}, and it follows ESPN from now on.`
    }
    case 'keep_value': {
      if (action.synced && action.espn) {
        const fresh = await pb.collection('clients').getOne<Record<string, any>>(action.clientId, { fields: 'profile_sync' })
        const sync = fresh.profile_sync && typeof fresh.profile_sync === 'object' ? fresh.profile_sync : {}
        await pb.collection('clients').update(action.clientId, { profile_sync: { ...sync, [action.field]: keepRecordValue(action.ours, action.espn, now) } })
      }
      if (action.ack) await acknowledge('clients', action.clientId, action.ack.check, action.ack.key, user.id)
      return `Kept ${action.ours || 'what is on file'}. Nothing asks again unless a source changes.`
    }
    case 'reverse_commission': {
      const { reversed } = await reverseBookedCommission(action.rowId, user.id)
      return `$${reversed.toLocaleString('en-US')} of booked commission reversed. The row keeps what it held in its notes.`
    }
    case 'log_arrived':
      await logMoneyItem({ item: action.item as any, amount: Number(action.item.amount), date: action.item.date, userId: user.id, agencyId: user.agency_id })
      return 'Recorded as arrived.'
    case 'confirm_schedule':
      await updateContract(action.contractId, { paycheck_weeks: action.weeks, paycheck_cadence: 'weekly' })
      return `Paid weekly over ${action.weeks} weeks. Its checks are now dated.`
    case 'contract_read':
      await decideContractRead(action.extractionId, action.decision)
      return action.decision === 'apply' ? "The document's terms are on the contract." : 'Rejected. The contract is unchanged.'
    case 'contact_suggestion':
      if (action.decision === 'accept') await acceptContactSuggestion(action.suggestionId)
      else await skipContactSuggestion(action.suggestionId)
      return action.decision === 'accept' ? 'Added.' : 'Skipped. It will not be suggested again.'
    case 'book_change':
      await decideBookChanges(action.decision, [action.changeId])
      return action.decision === 'approve' ? 'Approved. The Mac makes the change on its next run.' : 'Skipped.'
    case 'partner_inquiry': {
      await partnerPages({ action: 'inquiry', id: action.inquiryId, decision: action.decision })
      return action.decision === 'add' ? 'Added to the pipeline as a new deal at Prospect, with the brand and contact.' : 'Dismissed.'
    }
    case 'honor':
      return settleHonor(pb as any, action.findId, action.decision, user.id)
    case 'nil_go':
      await nilGoAction(action.dealId, { action: 'status', status: action.status as NilGoStatus })
      return `NIL Go status updated: ${action.label}.`
    case 'open':
      return ''
  }
}
